<script setup>
import { useBettingStats } from "@/composables/useBettingStats";
import { motion, AnimatePresence } from "motion-v";
import { useDevice } from "@/composables/useDevice";
import { useAuth } from "@/composables/useAuth";
import { computed, ref, watch, onUnmounted, onMounted, nextTick } from "vue";

// ===== Composables =====
const {
  bettingStats,
  upWinAmount,
  downWinAmount,
  gameResetCounter,
  animationStates,
} = useBettingStats();
const { isMobile } = useDevice();
const { isLoggedIn } = useAuth();

// ===== 계산된 값 =====
// 로그인 상태에 따른 최대 표시 인원 (비로그인: 6명, 로그인: 12명)
// 최대 표시 인원을 클라이언트에서만 결정
const maxDisplayCount = ref(6); // 초기값은 6명

// UP 참여자 총 수
const upTotalPeopleCount = computed(() => {
  return bettingStats.value.upParticipants.length;
});

// DOWN 참여자 총 수
const downTotalPeopleCount = computed(() => {
  return bettingStats.value.downParticipants.length;
});

// UP 표시할 참여자 목록 (로그인 상태에 따라 최대 인원 제한)
const upDisplayParticipants = computed(() => {
  return bettingStats.value.upParticipants.slice(0, maxDisplayCount.value);
});

// DOWN 표시할 참여자 목록 (로그인 상태에 따라 최대 인원 제한)
const downDisplayParticipants = computed(() => {
  return bettingStats.value.downParticipants.slice(0, maxDisplayCount.value);
});

// UP 더보기 버튼 표시 여부 (최대 표시 인원 초과 시)
const showUpMore = computed(() => {
  return bettingStats.value.upParticipants.length > maxDisplayCount.value;
});

// DOWN 더보기 버튼 표시 여부 (최대 표시 인원 초과 시)
const showDownMore = computed(() => {
  return bettingStats.value.downParticipants.length > maxDisplayCount.value;
});

// UP 더보기 버튼에 표시할 숫자 (전체 - 최대 표시 인원)
const upMoreCount = computed(() => {
  return bettingStats.value.upParticipants.length - maxDisplayCount.value;
});

// DOWN 더보기 버튼에 표시할 숫자 (전체 - 최대 표시 인원)
const downMoreCount = computed(() => {
  return bettingStats.value.downParticipants.length - maxDisplayCount.value;
});

// UP 더보기 버튼 preview에 표시할 참여자 (최대 표시 인원 다음부터 4명)
const upMorePreviewParticipants = computed(() => {
  return bettingStats.value.upParticipants.slice(
    maxDisplayCount.value,
    maxDisplayCount.value + 4
  );
});

// DOWN 더보기 버튼 preview에 표시할 참여자 (최대 표시 인원 다음부터 4명)
const downMorePreviewParticipants = computed(() => {
  return bettingStats.value.downParticipants.slice(
    maxDisplayCount.value,
    maxDisplayCount.value + 4
  );
});

// UP에 베팅한 총 금액
const upTotalAmount = computed(() => {
  return bettingStats.value.upParticipants.reduce(
    (sum, participant) => sum + participant.amount,
    0
  );
});

// DOWN에 베팅한 총 금액
const downTotalAmount = computed(() => {
  return bettingStats.value.downParticipants.reduce(
    (sum, participant) => sum + participant.amount,
    0
  );
});

// ===== 애니메이션 상태 =====

// ===== 애니메이션 상태 =====
// UP 애니메이션 완료 상태 (전역 상태와 동기화)
const upAnimationComplete = computed({
  get: () => animationStates.value.upAnimationComplete,
  set: (val) => {
    animationStates.value.upAnimationComplete = val;
  },
});
// DOWN 애니메이션 완료 상태 (전역 상태와 동기화)
const downAnimationComplete = computed({
  get: () => animationStates.value.downAnimationComplete,
  set: (val) => {
    animationStates.value.downAnimationComplete = val;
  },
});

// ===== 애니메이션 값 =====
// UP 금액 애니메이션 값
const upAnimationValue = ref(0);
// DOWN 금액 애니메이션 값
const downAnimationValue = ref(0);
// 승리 금액 애니메이션 값
const winAmountAnimationValue = ref(0);

// ===== 애니메이션 제어 =====
// UP 애니메이션 시작 여부
const upAnimationStarted = ref(false);
// DOWN 애니메이션 시작 여부
const downAnimationStarted = ref(false);
// 승리 금액 애니메이션 시작 여부
const winAmountAnimationStarted = ref(false);

// ===== 카운트 애니메이션 =====
// UP 카운트 애니메이션 완료 여부 (전역 상태와 동기화)
const upCountAnimationComplete = computed({
  get: () => animationStates.value.upCountAnimationComplete,
  set: (val) => {
    animationStates.value.upCountAnimationComplete = val;
  },
});
// DOWN 카운트 애니메이션 완료 여부 (전역 상태와 동기화)
const downCountAnimationComplete = computed({
  get: () => animationStates.value.downCountAnimationComplete,
  set: (val) => {
    animationStates.value.downCountAnimationComplete = val;
  },
});

// 두 카운트 애니메이션이 모두 완료되었는지 확인 (전역 상태와 동기화)
const bothCountAnimationsComplete = computed(() => {
  const upShowResult = bettingStats.value.upShowResult;
  const downShowResult = bettingStats.value.downShowResult;

  if (!upShowResult || !downShowResult) return false;

  const isComplete =
    upCountAnimationComplete.value && downCountAnimationComplete.value;
  animationStates.value.bothCountAnimationsComplete = isComplete;
  return isComplete;
});

// ===== 결과 영역 애니메이션 =====
// UP 결과 영역 width 확장 상태 (전역 상태와 동기화)
const upResultExpanded = computed({
  get: () => animationStates.value.upResultExpanded,
  set: (val) => {
    animationStates.value.upResultExpanded = val;
  },
});
// DOWN 결과 영역 width 확장 상태 (전역 상태와 동기화)
const downResultExpanded = computed({
  get: () => animationStates.value.downResultExpanded,
  set: (val) => {
    animationStates.value.downResultExpanded = val;
  },
});

// UP width 애니메이션 완료 여부 (전역 상태와 동기화)
const upWidthAnimationComplete = computed({
  get: () => animationStates.value.upWidthAnimationComplete,
  set: (val) => {
    animationStates.value.upWidthAnimationComplete = val;
  },
});
// DOWN width 애니메이션 완료 여부 (전역 상태와 동기화)
const downWidthAnimationComplete = computed({
  get: () => animationStates.value.downWidthAnimationComplete,
  set: (val) => {
    animationStates.value.downWidthAnimationComplete = val;
  },
});

// ===== 애니메이션 타이머 =====
// UP 애니메이션 타이머 저장 (정리용)
const upAnimationTimers = ref([]);
// DOWN 애니메이션 타이머 저장 (정리용)
const downAnimationTimers = ref([]);
// 승리 금액 애니메이션 타이머 저장 (정리용)
const winAmountAnimationTimers = ref([]);
// 승리 UI 자동 숨김 타이머 저장 (정리용)
const winUIHideTimer = ref(null);

// ===== Info Group 렌더링 상태 =====
// UP info-group 렌더링 완료 여부
const upInfoGroupRendered = ref(false);
// DOWN info-group 렌더링 완료 여부
const downInfoGroupRendered = ref(false);

// ===== 모달 상태 =====
// 참여자 더보기 모달 열림 상태
const isMoreModalOpen = ref(false);
// 선택된 방향 ('up' | 'down')
const selectedDirection = ref(null);

// 모달 열기
const openMoreModal = (direction) => {
  selectedDirection.value = direction;
  isMoreModalOpen.value = true;
};

// 모달 닫기
const closeMoreModal = () => {
  isMoreModalOpen.value = false;
  selectedDirection.value = null;
};

// 선택된 방향의 참여자 목록 (최대 표시 인원 다음부터)
const selectedParticipants = computed(() => {
  if (selectedDirection.value === "up") {
    return bettingStats.value.upParticipants.slice(maxDisplayCount.value);
  } else if (selectedDirection.value === "down") {
    return bettingStats.value.downParticipants.slice(maxDisplayCount.value);
  }
  return [];
});

// 모달 제목
const modalTitle = computed(() => {
  return selectedDirection.value === "up" ? "UP PLAYERS" : "DOWN PLAYERS";
});

// ===== 트랜지션 설정 =====
// Content 영역 사라지는 트랜지션
const contentExitTransition = {
  type: "spring",
  stiffness: 300,
  damping: 20,
  mass: 0.8,
  bounce: 0.2,
};

// 즉시 전환 트랜지션 (애니메이션 없음)
const instantTransition = {
  duration: 0,
};

// Result 영역 나타나는 트랜지션
const resultEnterTransition = {
  type: "spring",
  stiffness: 250,
  damping: 20,
  mass: 1,
  bounce: 0.2,
};

// ===== 승자 여부 =====
// UP이 승자인지 여부
const isUpWinner = computed(() => bettingStats.value.winner === "up");
// DOWN이 승자인지 여부
const isDownWinner = computed(() => bettingStats.value.winner === "down");

// ===== 애니메이션 완료 여부 =====
// UP 애니메이션 완전 완료 여부 (width 애니메이션까지 완료된 경우)
const upAnimationFullyComplete = computed(() => {
  return animationStates.value.upWidthAnimationComplete;
});

// DOWN 애니메이션 완전 완료 여부 (width 애니메이션까지 완료된 경우)
const downAnimationFullyComplete = computed(() => {
  return animationStates.value.downWidthAnimationComplete;
});

// ===== 애니메이션 함수 =====
// 금액 카운트 애니메이션 시작 (승자/패자 공통)
const startAmountAnimation = (startValue, targetValue, isUp, onComplete) => {
  // 기존 타이머 정리
  const timers = isUp ? upAnimationTimers : downAnimationTimers;
  timers.value.forEach((timer) => clearTimeout(timer));
  timers.value = [];

  if (startValue === targetValue) {
    if (isUp) {
      upAnimationValue.value = targetValue;
    } else {
      downAnimationValue.value = targetValue;
    }
    // 즉시 완료 콜백 실행
    if (onComplete) {
      onComplete();
    }
    return;
  }

  // 목표값까지 애니메이션
  const diff = Math.abs(targetValue - startValue);
  const each = Math.ceil(diff / 50);
  const isIncreasing = targetValue > startValue;
  const stepDelay = 20;
  let time = 0;
  let lastTimer = null;

  const updateValue = (value) => {
    if (isUp) {
      upAnimationValue.value = value;
    } else {
      downAnimationValue.value = value;
    }
  };

  if (isIncreasing) {
    // 증가 애니메이션 (승자)
    for (let i = startValue; i <= targetValue; i += each) {
      const timer = setTimeout(() => {
        updateValue(i);
      }, stepDelay * time);

      timers.value.push(timer);
      lastTimer = timer;

      // 딱 떨어지지 않는 숫자를 마지막에 목표값으로 만들어주기 위함
      if (i + each > targetValue) {
        const finalTimer = setTimeout(() => {
          updateValue(targetValue);
          // 애니메이션 완료 콜백 실행
          if (onComplete) {
            onComplete();
          }
        }, stepDelay * (time + 1));
        timers.value.push(finalTimer);
        lastTimer = finalTimer;
      }
      time++;
    }
  } else {
    // 감소 애니메이션 (패자)
    for (let i = startValue; i >= targetValue; i -= each) {
      const timer = setTimeout(() => {
        updateValue(i);
      }, stepDelay * time);

      timers.value.push(timer);
      lastTimer = timer;

      // 딱 떨어지지 않는 숫자를 마지막에 목표값으로 만들어주기 위함
      if (i - each < targetValue) {
        const finalTimer = setTimeout(() => {
          updateValue(targetValue);
          // 애니메이션 완료 콜백 실행
          if (onComplete) {
            onComplete();
          }
        }, stepDelay * (time + 1));
        timers.value.push(finalTimer);
        lastTimer = finalTimer;
      }
      time++;
    }
  }
};

// 승리 금액 카운트 애니메이션 시작
const startWinAmountAnimation = (targetValue, onComplete) => {
  // 기존 타이머 정리
  winAmountAnimationTimers.value.forEach((timer) => clearTimeout(timer));
  winAmountAnimationTimers.value = [];

  const startValue = 0;

  if (targetValue === 0) {
    winAmountAnimationValue.value = 0;
    if (onComplete) {
      onComplete();
    }
    return;
  }

  // 목표값까지 애니메이션
  const diff = targetValue;
  const each = Math.ceil(diff / 50);
  const stepDelay = 30;
  let time = 0;

  for (let i = startValue; i <= targetValue; i += each) {
    const timer = setTimeout(() => {
      winAmountAnimationValue.value = i;
    }, stepDelay * time);

    winAmountAnimationTimers.value.push(timer);

    if (i + each > targetValue) {
      const finalTimer = setTimeout(() => {
        winAmountAnimationValue.value = targetValue;
        // 애니메이션 완료 콜백 실행
        if (onComplete) {
          onComplete();
        }
      }, stepDelay * (time + 1));
      winAmountAnimationTimers.value.push(finalTimer);
    }
    time++;
  }
};

// 숫자 포맷팅 함수 (콤마 추가)
const formatNumber = (num) => {
  if (!num && num !== 0) return "";
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// ===== 표시 값 =====
// UP 화면 표시 금액 (K 단위, 애니메이션 시작 후 애니메이션 값 사용)
const upDisplayAmountK = computed(() => {
  const base = Math.floor(upTotalAmount.value / 1000);
  if (!bettingStats.value.upShowResult) return formatNumber(base);
  const value = upAnimationStarted.value ? upAnimationValue.value : base;
  return formatNumber(value);
});

// DOWN 화면 표시 금액 (K 단위, 애니메이션 시작 후 애니메이션 값 사용)
const downDisplayAmountK = computed(() => {
  const base = Math.floor(downTotalAmount.value / 1000);
  if (!bettingStats.value.downShowResult) return formatNumber(base);
  const value = downAnimationStarted.value ? downAnimationValue.value : base;
  return formatNumber(value);
});

// 내가 베팅한 쪽이 이겼는지 확인
const hasMyBetting = computed(() => {
  return (
    bettingStats.value.upTotalBetting > 0 ||
    bettingStats.value.downTotalBetting > 0
  );
});

const myBettingWon = computed(() => {
  if (!hasMyBetting.value) return false;

  const upBet = bettingStats.value.upTotalBetting > 0;
  const downBet = bettingStats.value.downTotalBetting > 0;

  if (upBet && isUpWinner.value) return true;
  if (downBet && isDownWinner.value) return true;

  return false;
});

// 내가 실제로 얻은 금액 계산 (베팅 금액의 20% 수익)
const myWinAmount = computed(() => {
  if (!myBettingWon.value) return 0;

  if (bettingStats.value.upTotalBetting > 0 && isUpWinner.value) {
    // UP에 베팅했고 UP이 이겼을 때: 내 베팅 금액의 20% 수익
    return Math.floor(bettingStats.value.upTotalBetting * 0.2);
  } else if (bettingStats.value.downTotalBetting > 0 && isDownWinner.value) {
    // DOWN에 베팅했고 DOWN이 이겼을 때: 내 베팅 금액의 20% 수익
    return Math.floor(bettingStats.value.downTotalBetting * 0.2);
  }

  return 0;
});

// 승리 UI 표시 여부
const showWinUI = ref(false);

// 승리 금액 화면 표시 (포맷팅된 값)
const displayWinAmount = computed(() => {
  return formatNumber(winAmountAnimationValue.value);
});

// UP Content 영역 애니메이션 중 여부
const upContentAnimating = computed(() => {
  return bettingStats.value.upShowResult;
});

// DOWN Content 영역 애니메이션 중 여부
const downContentAnimating = computed(() => {
  return bettingStats.value.downShowResult;
});

// ===== 애니메이션 핸들러 =====
// showResult 변경 시 애니메이션 처리 (UP/DOWN 통합 함수)
const handleResultAnimation = (isUp) => {
  const showResult = isUp
    ? bettingStats.value.upShowResult
    : bettingStats.value.downShowResult;
  const animationComplete = isUp ? upAnimationComplete : downAnimationComplete;
  const animationStarted = isUp ? upAnimationStarted : downAnimationStarted;
  const animationValue = isUp ? upAnimationValue : downAnimationValue;
  const animationTimers = isUp ? upAnimationTimers : downAnimationTimers;
  const totalAmount = isUp ? upTotalAmount : downTotalAmount;
  const isWinner = isUp ? isUpWinner : isDownWinner;
  const winAmount = isUp ? upWinAmount : downWinAmount;
  const countAnimationComplete = isUp
    ? upCountAnimationComplete
    : downCountAnimationComplete;
  const resultExpanded = isUp ? upResultExpanded : downResultExpanded;

  if (!showResult) {
    // 초기화
    animationComplete.value = false;
    animationStarted.value = false;
    animationValue.value = 0;
    countAnimationComplete.value = false;
    resultExpanded.value = false;
    if (isUp) {
      upWidthAnimationComplete.value = false;
    } else {
      downWidthAnimationComplete.value = false;
    }
    // 타이머 정리
    animationTimers.value.forEach((timer) => clearTimeout(timer));
    animationTimers.value = [];
  } else {
    // result 애니메이션이 시작되면 약간의 delay 후 blob/value 시작
    setTimeout(() => {
      if (showResult && !animationComplete.value) {
        animationComplete.value = true;
      }
      // 애니메이션이 어느 정도 끝난 뒤 금액 애니메이션 시작
      setTimeout(() => {
        if (showResult) {
          const baseAmount = Math.floor(totalAmount.value / 1000);
          const targetAmount = isWinner.value
            ? Math.floor(winAmount.value / 1000) // 승자: 이긴 금액
            : 0; // 패자: 0

          animationValue.value = baseAmount; // 초기값 설정

          // 카운트 애니메이션 완료 콜백
          const onCountAnimationComplete = () => {
            countAnimationComplete.value = true;
            // 두 카운트 애니메이션이 모두 완료되었는지 확인은 watch에서 처리
          };

          startAmountAnimation(
            baseAmount,
            targetAmount,
            isUp,
            onCountAnimationComplete
          );
          animationStarted.value = true;
        }
      }, 800); // value가 충분히 노출된 뒤 애니메이션 시작
    }, 200); // result 애니메이션의 약 50-60% 지점에서 시작
  }
};

// ===== Watchers =====
// UP showResult 변경 감지 (애니메이션 완료 상태 초기화 및 blob/value 시작)
// 단, 이미 완료된 애니메이션이면 다시 실행하지 않음
watch(
  () => bettingStats.value.upShowResult,
  (newVal, oldVal) => {
    if (!newVal) {
      // showResult가 false가 되면 초기화만 수행 (애니메이션 재실행 안 함)
      return;
    }

    // 애니메이션 시작 시 모달이 열려있으면 닫기
    if (isMoreModalOpen.value) {
      closeMoreModal();
    }

    // 애니메이션 시작 시 info-group 렌더링 상태 제거
    upInfoGroupRendered.value = false;

    // showResult가 true이고, 아직 애니메이션이 완료되지 않았을 때만 실행
    if (!animationStates.value.upAnimationComplete) {
      handleResultAnimation(true);
    } else {
      // 이미 완료된 상태면 즉시 완료 상태로 설정
      upAnimationStarted.value = true;
      upAnimationValue.value = isUpWinner.value
        ? Math.floor(upWinAmount.value / 1000)
        : 0;
    }
  }
);

// DOWN showResult 변경 감지 (애니메이션 완료 상태 초기화 및 blob/value 시작)
// 단, 이미 완료된 애니메이션이면 다시 실행하지 않음
watch(
  () => bettingStats.value.downShowResult,
  (newVal, oldVal) => {
    if (!newVal) {
      // showResult가 false가 되면 초기화만 수행 (애니메이션 재실행 안 함)
      return;
    }

    // 애니메이션 시작 시 모달이 열려있으면 닫기
    if (isMoreModalOpen.value) {
      closeMoreModal();
    }

    // 애니메이션 시작 시 info-group 렌더링 상태 제거
    downInfoGroupRendered.value = false;

    // showResult가 true이고, 아직 애니메이션이 완료되지 않았을 때만 실행
    if (!animationStates.value.downAnimationComplete) {
      handleResultAnimation(false);
    } else {
      // 이미 완료된 상태면 즉시 완료 상태로 설정
      downAnimationStarted.value = true;
      downAnimationValue.value = isDownWinner.value
        ? Math.floor(downWinAmount.value / 1000)
        : 0;
    }
  }
);

// 두 카운트 애니메이션이 모두 완료되면 width/opacity 애니메이션 시작
watch(bothCountAnimationsComplete, (isComplete, oldVal) => {
  // 이미 완료된 상태면 다시 실행하지 않음
  if (isComplete && !oldVal) {
    // 두 애니메이션이 모두 완료된 후 1초 뒤 width/opacity 애니메이션 시작
    setTimeout(() => {
      if (
        bettingStats.value.upShowResult &&
        !animationStates.value.upResultExpanded
      ) {
        upResultExpanded.value = true;
      }
      if (
        bettingStats.value.downShowResult &&
        !animationStates.value.downResultExpanded
      ) {
        downResultExpanded.value = true;
      }

      // width 애니메이션 완료 감지 (spring 애니메이션 대략 500ms 후)
      setTimeout(() => {
        if (
          bettingStats.value.upShowResult &&
          !animationStates.value.upWidthAnimationComplete
        ) {
          upWidthAnimationComplete.value = true;
        }
        if (
          bettingStats.value.downShowResult &&
          !animationStates.value.downWidthAnimationComplete
        ) {
          downWidthAnimationComplete.value = true;
        }

        // width 애니메이션 완료 후 승리 금액 카운트 애니메이션 시작 (내가 이겼을 때만)
        if (!winAmountAnimationStarted.value && myBettingWon.value) {
          const targetAmount = myWinAmount.value;
          startWinAmountAnimation(targetAmount, () => {
            winAmountAnimationStarted.value = true;
            // 승리 금액 애니메이션 완료 후 2초 뒤에 UI 숨김
            if (winUIHideTimer.value) {
              clearTimeout(winUIHideTimer.value);
            }
            winUIHideTimer.value = setTimeout(() => {
              showWinUI.value = false;
              winUIHideTimer.value = null;
            }, 2000);
          });
        }
      }, 500);
    }, 1000);
  }
});

// 로그인 상태 변경 감지
watch(isLoggedIn, (newValue) => {
  maxDisplayCount.value = newValue ? 12 : 6;
});

// gameResetCounter 변경 시 승리 금액 애니메이션 초기화
watch(gameResetCounter, () => {
  winAmountAnimationValue.value = 0;
  winAmountAnimationStarted.value = false;
  winAmountAnimationTimers.value.forEach((timer) => clearTimeout(timer));
  winAmountAnimationTimers.value = [];
  // 승리 UI도 초기화
  showWinUI.value = false;
  if (winUIHideTimer.value) {
    clearTimeout(winUIHideTimer.value);
    winUIHideTimer.value = null;
  }
});

// 승리 UI 표시 조건 감지 및 자동 숨김 처리
watch(
  [
    () => upWidthAnimationComplete.value,
    () => downWidthAnimationComplete.value,
    () => myBettingWon.value,
  ],
  ([upComplete, downComplete, won]) => {
    const shouldShow =
      (upComplete || downComplete) && won;

    if (shouldShow && !showWinUI.value) {
      // 승리 UI 표시
      showWinUI.value = true;

      // 기존 타이머 정리
      if (winUIHideTimer.value) {
        clearTimeout(winUIHideTimer.value);
        winUIHideTimer.value = null;
      }

      // 만약 애니메이션이 이미 완료된 상태라면 (이미 마운트된 경우)
      if (winAmountAnimationStarted.value) {
        // 즉시 2초 후에 숨김
        winUIHideTimer.value = setTimeout(() => {
          showWinUI.value = false;
          winUIHideTimer.value = null;
        }, 2000);
      }
      // 그렇지 않으면 애니메이션 완료 콜백에서 타이머가 설정됨
    } else if (!shouldShow && showWinUI.value) {
      // 조건이 만족되지 않으면 즉시 숨김
      showWinUI.value = false;
      if (winUIHideTimer.value) {
        clearTimeout(winUIHideTimer.value);
        winUIHideTimer.value = null;
      }
    }
  },
  { immediate: true }
);

// ===== 애니메이션 완료 핸들러 =====
// UP 애니메이션 완료 핸들러 (이미 시작되었으면 무시)
const handleUpAnimationComplete = (definition) => {
  // watch에서 이미 시작했을 수 있으므로 중복 방지
  if (bettingStats.value.upShowResult && !upAnimationComplete.value) {
    upAnimationComplete.value = true;
  }
};

// DOWN 애니메이션 완료 핸들러 (이미 시작되었으면 무시)
const handleDownAnimationComplete = (definition) => {
  if (bettingStats.value.downShowResult && !downAnimationComplete.value) {
    downAnimationComplete.value = true;
  }
};

// ===== Lifecycle Hooks =====
// 컴포넌트 마운트 시 이미 완료된 애니메이션 상태 복원
onMounted(() => {
  // 클라이언트에서 마운트된 후 로그인 상태 확인
  maxDisplayCount.value = isLoggedIn.value ? 12 : 6;

  // showResult가 true이고 이미 애니메이션이 완료된 상태면 즉시 완료 상태로 설정
  if (
    bettingStats.value.upShowResult &&
    animationStates.value.upAnimationComplete
  ) {
    upAnimationStarted.value = true;
    upAnimationValue.value = isUpWinner.value
      ? Math.floor(upWinAmount.value / 1000)
      : 0;
  }

  if (
    bettingStats.value.downShowResult &&
    animationStates.value.downAnimationComplete
  ) {
    downAnimationStarted.value = true;
    downAnimationValue.value = isDownWinner.value
      ? Math.floor(downWinAmount.value / 1000)
      : 0;
  }

  // 렌더링 완료 후 info-group 클래스 추가
  nextTick(() => {
    setTimeout(() => {
      upInfoGroupRendered.value = true;
      downInfoGroupRendered.value = true;
    }, 100);
  });
});

// gameResetCounter 변경 시 info-group 렌더링 상태 초기화 및 재설정
watch(gameResetCounter, () => {
  upInfoGroupRendered.value = false;
  downInfoGroupRendered.value = false;

  nextTick(() => {
    setTimeout(() => {
      upInfoGroupRendered.value = true;
      downInfoGroupRendered.value = true;
    }, 100);
  });
});

// 컴포넌트 언마운트 시 타이머 정리
onUnmounted(() => {
  upAnimationTimers.value.forEach((timer) => clearTimeout(timer));
  downAnimationTimers.value.forEach((timer) => clearTimeout(timer));
  winAmountAnimationTimers.value.forEach((timer) => clearTimeout(timer));
  if (winUIHideTimer.value) {
    clearTimeout(winUIHideTimer.value);
    winUIHideTimer.value = null;
  }
  upAnimationTimers.value = [];
  downAnimationTimers.value = [];
  winAmountAnimationTimers.value = [];
});
</script>

<template>
  <!-- 데스크탑 버전 -->
  <template v-if="!isMobile">
    <!-- UP 배팅 상태 영역 -->
    <div class="betting-status__list betting-status__list--up">
      <!-- UP Content 영역 (참여자 목록) -->
      <motion.div
        :key="`up-content-${gameResetCounter}`"
        class="betting-status__content"
        :class="{ 'betting-status__content--animating': upContentAnimating }"
        :initial="{ scaleY: 1, opacity: 1, y: 0 }"
        :animate="
          bettingStats.upShowResult
            ? { scaleY: 0.8, opacity: 0, y: 80 }
            : { scaleY: 1, opacity: 1, y: 0 }
        "
        :transition="
          bettingStats.upShowResult ? contentExitTransition : instantTransition
        "
        style="transform-origin: bottom"
      >
        <!-- UP 제목 -->
        <div class="betting-status__title ico-after ico-up-white">
          <strong class="betting-status__title-text">UP</strong>
        </div>
        <!-- UP 정보 (참여자 수, 총 배팅금) -->
        <div
          class="betting-status__info"
          :class="{ 'betting-status__info--rendered': upInfoGroupRendered }"
        >
          <div class="betting-status__info-group">
            <span
              class="betting-status__info-text betting-status__info-text--person ico-before ico-user-primary"
            >
              <CommonRollingCounter
                :value="upTotalPeopleCount"
                typography="p1"
              />
            </span>
            <span
              class="betting-status__info-text betting-status__info-text--price ico-before ico-prefix-primary"
            >
              <CommonRollingCounter
                :value="Math.floor(bettingStats.upWin / 1000)"
                suffix="K"
                typography="p1"
              />
            </span>
          </div>
        </div>
        <!-- UP 참여자 목록 -->
        <div class="betting-status__users">
          <AnimatePresence>
            <motion.div
              v-for="(participant, index) in upDisplayParticipants"
              :key="participant.id"
              class="betting-status__item"
              :class="{ 'betting-status__item--my': participant.isMy }"
              :initial="{
                x: -100,
                opacity: 0,
              }"
              :animate="{
                x: 0,
                opacity: 1,
              }"
              :exit="
                bettingStats.upParticipants.length > maxDisplayCount &&
                index === maxDisplayCount - 1
                  ? { duration: 0 }
                  : {
                      opacity: 0,
                      x: -100,
                    }
              "
              :transition="{
                type: 'spring',
                stiffness: 200,
                damping: 18,
                mass: 0.8,
                bounce: 0.3,
              }"
            >
              <img
                src="@/assets/images/user.png"
                :alt="participant.name"
                class="betting-status__item-thumb"
              />
              <p class="betting-status__item-name">{{ participant.name }}</p>
              <strong class="betting-status__item-amount">
                <CommonRollingCounter
                  :value="Math.floor(participant.amount / 1000)"
                  suffix="K"
                  typography="p1"
                />
              </strong>
            </motion.div>
          </AnimatePresence>
        </div>

        <!-- UP 참여자 더보기 -->
        <div
          v-if="showUpMore"
          class="betting-status__more"
          role="button"
          @click="openMoreModal('up')"
        >
          <div class="betting-status__more-preview">
            <img
              v-for="participant in upMorePreviewParticipants"
              :key="participant.id"
              src="@/assets/images/user.png"
              :alt="participant.name"
            />
          </div>
          <div class="betting-status__more-text ico-after ico-chevron-right">
            <span>{{ upMoreCount }}</span>
            <span>+</span>
          </div>
        </div>
      </motion.div>

      <!-- UP 결과 영역 (게임 종료 시 표시) -->
      <motion.div
        v-if="bettingStats.upShowResult"
        class="betting-status__result"
        :class="{ 'betting-status__result--complete': upAnimationComplete }"
        :initial="{ scale: 0.85, opacity: 0, y: -30, width: '100%' }"
        :animate="
          upAnimationFullyComplete
            ? isUpWinner
              ? { scale: 1, opacity: 1, y: 0, width: 'calc(200% + 1.2rem)' }
              : { scale: 1, opacity: 1, y: 0, width: '0' }
            : bothCountAnimationsComplete
            ? isUpWinner
              ? {
                  scale: 1,
                  opacity: 1,
                  y: 0,
                  width: upResultExpanded ? 'calc(200% + 1.2rem)' : '100%',
                }
              : {
                  scale: 1,
                  opacity: 1,
                  y: 0,
                  width: upResultExpanded ? '0' : '100%',
                }
            : { scale: 1, opacity: 1, y: 0, width: '100%' }
        "
        :transition="
          upAnimationFullyComplete
            ? { duration: 0 }
            : bothCountAnimationsComplete && upResultExpanded
            ? { type: 'spring', stiffness: 250, damping: 30, mass: 0.8 }
            : resultEnterTransition
        "
        @animation-complete="handleUpAnimationComplete"
        style="transform-origin: center; overflow: hidden"
      >
        <!-- 결과 배경 blob -->
        <AnimatePresence>
          <motion.i
            v-if="upAnimationComplete"
            key="blob"
            class="betting-status__result-blob"
            :initial="
              upAnimationFullyComplete
                ? { scale: 1, opacity: 0.6 }
                : { scale: 0, opacity: 0 }
            "
            :animate="{ scale: 1, opacity: 0.6 }"
            :transition="
              upAnimationFullyComplete
                ? { duration: 0 }
                : {
                    type: 'spring',
                    stiffness: 300,
                    damping: 20,
                    mass: 0.5,
                    bounce: 0.2,
                    delay: 0.55,
                  }
            "
          >
          </motion.i>
        </AnimatePresence>
        <!-- 결과 값 영역 -->
        <div class="betting-status__result-group">
          <AnimatePresence>
            <motion.div
              v-if="upAnimationComplete"
              key="value"
              class="betting-status__result-value"
              :initial="
                upAnimationFullyComplete
                  ? bothCountAnimationsComplete &&
                    !isUpWinner &&
                    upResultExpanded
                    ? { scale: 1, opacity: 0 }
                    : { scale: 1, opacity: 1 }
                  : { scale: 0, opacity: 0 }
              "
              :animate="
                bothCountAnimationsComplete && !isUpWinner && upResultExpanded
                  ? { scale: 1, opacity: 0 }
                  : { scale: 1, opacity: 1 }
              "
              :transition="
                upAnimationFullyComplete
                  ? { duration: 0 }
                  : bothCountAnimationsComplete &&
                    !isUpWinner &&
                    upResultExpanded
                  ? {
                      scale: {
                        type: 'spring',
                        stiffness: 300,
                        damping: 20,
                        mass: 0.5,
                        bounce: 0.2,
                        delay: 0.4,
                      },
                      opacity: { duration: 0 },
                    }
                  : {
                      type: 'spring',
                      stiffness: 300,
                      damping: 20,
                      mass: 0.5,
                      bounce: 0.2,
                      delay: 0.4,
                    }
              "
            >
              <strong class="betting-status__result-value-title">{{
                isUpWinner ? "승자" : "패자"
              }}</strong>
              <div
                class="betting-status__result-value-people ico-before ico-user-white"
              >
                <span>{{ upTotalPeopleCount }}</span>
              </div>
              <div
                class="betting-status__result-value-amount ico-before ico-prefix-white"
              >
                <strong>{{ upDisplayAmountK }}<span>K</span></strong>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>

    <!-- DOWN 배팅 상태 영역 -->
    <div class="betting-status__list betting-status__list--down">
      <!-- DOWN Content 영역 (참여자 목록) -->
      <motion.div
        :key="`down-content-${gameResetCounter}`"
        class="betting-status__content"
        :class="{
          'betting-status__content--animating': downContentAnimating,
        }"
        :initial="{ scaleY: 1, opacity: 1, y: 0 }"
        :animate="
          bettingStats.downShowResult
            ? { scaleY: 0.8, opacity: 0, y: 80 }
            : { scaleY: 1, opacity: 1, y: 0 }
        "
        :transition="
          bettingStats.downShowResult
            ? contentExitTransition
            : instantTransition
        "
        style="transform-origin: bottom"
      >
        <!-- DOWN 제목 -->
        <div class="betting-status__title ico-after ico-down-white">
          <strong class="betting-status__title-text">DOWN</strong>
        </div>
        <!-- DOWN 정보 (참여자 수, 총 배팅금) -->
        <div
          class="betting-status__info"
          :class="{ 'betting-status__info--rendered': downInfoGroupRendered }"
        >
          <div class="betting-status__info-group">
            <span
              class="betting-status__info-text betting-status__info-text--person ico-before ico-user-secondary"
            >
              <CommonRollingCounter
                :value="downTotalPeopleCount"
                typography="p1"
              />
            </span>
            <span
              class="betting-status__info-text betting-status__info-text--price ico-before ico-prefix-secondary"
            >
              <CommonRollingCounter
                :value="Math.floor(bettingStats.downLose / 1000)"
                suffix="K"
                typography="p1"
              />
            </span>
          </div>
        </div>
        <!-- DOWN 참여자 목록 -->
        <div class="betting-status__users">
          <AnimatePresence>
            <motion.div
              v-for="(participant, index) in downDisplayParticipants"
              :key="participant.id"
              class="betting-status__item"
              :class="{ 'betting-status__item--my': participant.isMy }"
              :initial="{
                x: 200,
                opacity: 0,
              }"
              :animate="{
                x: 0,
                opacity: 1,
              }"
              :exit="
                bettingStats.downParticipants.length > maxDisplayCount &&
                index === maxDisplayCount - 1
                  ? { duration: 0 }
                  : {
                      opacity: 0,
                      x: 200,
                    }
              "
              :transition="{
                type: 'spring',
                stiffness: 200,
                damping: 18,
                mass: 0.8,
                bounce: 0.3,
              }"
            >
              <img
                src="@/assets/images/user.png"
                :alt="participant.name"
                class="betting-status__item-thumb"
              />
              <p class="betting-status__item-name">{{ participant.name }}</p>
              <strong class="betting-status__item-amount">
                <CommonRollingCounter
                  :value="Math.floor(participant.amount / 1000)"
                  suffix="K"
                  typography="p1"
                />
              </strong>
            </motion.div>
          </AnimatePresence>
        </div>

        <!-- DOWN 참여자 더보기 -->
        <div
          v-if="showDownMore"
          class="betting-status__more"
          role="button"
          @click="openMoreModal('down')"
        >
          <div class="betting-status__more-preview">
            <img
              v-for="participant in downMorePreviewParticipants"
              :key="participant.id"
              src="@/assets/images/user.png"
              :alt="participant.name"
            />
          </div>
          <div class="betting-status__more-text ico-after ico-chevron-right">
            <span>{{ downMoreCount }}</span>
            <span>+</span>
          </div>
        </div>
      </motion.div>

      <!-- DOWN 결과 영역 (게임 종료 시 표시) -->
      <motion.div
        v-if="bettingStats.downShowResult"
        class="betting-status__result"
        :class="{ 'betting-status__result--complete': downAnimationComplete }"
        :initial="{ scale: 0.85, opacity: 0, y: -30, width: '100%' }"
        :animate="
          downAnimationFullyComplete
            ? isDownWinner
              ? { scale: 1, opacity: 1, y: 0, width: 'calc(200% + 1.2rem)' }
              : { scale: 1, opacity: 1, y: 0, width: '0' }
            : bothCountAnimationsComplete
            ? isDownWinner
              ? {
                  scale: 1,
                  opacity: 1,
                  y: 0,
                  width: downResultExpanded ? 'calc(200% + 1.2rem)' : '100%',
                }
              : {
                  scale: 1,
                  opacity: 1,
                  y: 0,
                  width: downResultExpanded ? '0' : '100%',
                }
            : { scale: 1, opacity: 1, y: 0, width: '100%' }
        "
        :transition="
          downAnimationFullyComplete
            ? { duration: 0 }
            : bothCountAnimationsComplete && downResultExpanded
            ? { type: 'spring', stiffness: 250, damping: 30, mass: 0.8 }
            : resultEnterTransition
        "
        @animation-complete="handleDownAnimationComplete"
        style="transform-origin: center; overflow: hidden"
      >
        <!-- 결과 배경 blob -->
        <AnimatePresence>
          <motion.i
            v-if="downAnimationComplete"
            key="blob"
            class="betting-status__result-blob"
            :initial="
              downAnimationFullyComplete
                ? { scale: 1, opacity: 0.6 }
                : { scale: 0, opacity: 0 }
            "
            :animate="{ scale: 1, opacity: 0.6 }"
            :transition="
              downAnimationFullyComplete
                ? { duration: 0 }
                : {
                    type: 'spring',
                    stiffness: 300,
                    damping: 20,
                    mass: 0.5,
                    bounce: 0.2,
                    delay: 0.55,
                  }
            "
          >
          </motion.i>
        </AnimatePresence>
        <!-- 결과 값 영역 -->
        <div class="betting-status__result-group">
          <AnimatePresence>
            <motion.div
              v-if="downAnimationComplete"
              key="value"
              class="betting-status__result-value"
              :initial="
                downAnimationFullyComplete
                  ? bothCountAnimationsComplete &&
                    !isDownWinner &&
                    downResultExpanded
                    ? { scale: 1, opacity: 0 }
                    : { scale: 1, opacity: 1 }
                  : { scale: 0, opacity: 0 }
              "
              :animate="
                bothCountAnimationsComplete &&
                !isDownWinner &&
                downResultExpanded
                  ? { scale: 1, opacity: 0 }
                  : { scale: 1, opacity: 1 }
              "
              :transition="
                downAnimationFullyComplete
                  ? { duration: 0 }
                  : bothCountAnimationsComplete &&
                    !isDownWinner &&
                    downResultExpanded
                  ? {
                      scale: {
                        type: 'spring',
                        stiffness: 300,
                        damping: 20,
                        mass: 0.5,
                        bounce: 0.2,
                        delay: 0.4,
                      },
                      opacity: { duration: 0 },
                    }
                  : {
                      type: 'spring',
                      stiffness: 300,
                      damping: 20,
                      mass: 0.5,
                      bounce: 0.2,
                      delay: 0.4,
                    }
              "
            >
              <strong class="betting-status__result-value-title">{{
                isDownWinner ? "승자" : "패자"
              }}</strong>
              <!-- 결과 참여자 수 -->
              <div
                class="betting-status__result-value-people ico-before ico-user-white"
              >
                <span>{{ downTotalPeopleCount }}</span>
              </div>
              <!-- 결과 금액 -->
              <div
                class="betting-status__result-value-amount ico-before ico-prefix-white"
              >
                <strong>{{ downDisplayAmountK }}<span>K</span></strong>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>

    <!-- 참여자 더보기 모달 -->
    <AnimatePresence>
      <motion.div
        v-if="isMoreModalOpen"
        key="more-modal"
        class="betting-status__more-modal"
        :initial="{ opacity: 0, y: 80, scale: 0.9 }"
        :animate="{ opacity: 1, y: 0, scale: 1 }"
        :exit="{ opacity: 0, y: 30, scale: 0.95 }"
        :transition="{
          type: 'spring',
          stiffness: 300,
          damping: 15,
          mass: 0.4,
          bounce: 0.4,
        }"
      >
        <div class="betting-status__more-modal-header">
          <strong>{{ modalTitle }}</strong>
          <button type="button" class="ico-close" @click="closeMoreModal">
            <span class="blind">닫기</span>
          </button>
        </div>
        <div class="betting-status__more-modal-body">
          <div
            v-for="participant in selectedParticipants"
            :key="participant.id"
            class="betting-status__more-modal-item"
          >
            <img src="@/assets/images/user.png" alt="" />
            <strong>
              <CommonRollingCounter
                :value="Math.floor(participant.amount / 1000)"
                suffix="K"
                typography="p1"
              />
            </strong>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  </template>

  <!-- 모바일 버전 -->
  <template v-else>
    <!-- UP 배팅 상태 영역 (모바일) -->
    <div class="betting-status__list betting-status__list--up">
      <!-- UP Content 영역 (참여자 목록) -->
      <motion.div
        :key="`up-content-mobile-${gameResetCounter}`"
        class="betting-status__content"
        :class="{ 'betting-status__content--animating': upContentAnimating }"
        :initial="{ scale: 1, opacity: 1 }"
        :animate="
          bettingStats.upShowResult
            ? { scale: 0.8, opacity: 0 }
            : { scale: 1, opacity: 1 }
        "
        :transition="
          bettingStats.upShowResult ? contentExitTransition : instantTransition
        "
        style="transform-origin: center"
      >
        <!-- UP 정보 (참여자 수, 총 배팅금) -->
        <div
          class="betting-status__info"
          :class="{ 'betting-status__info--rendered': upInfoGroupRendered }"
        >
          <span
            class="betting-status__info-text betting-status__info-text--person ico-before ico-user-primary"
          >
            <CommonRollingCounter :value="upTotalPeopleCount" typography="p1" />
          </span>
          <span
            class="betting-status__info-text betting-status__info-text--price ico-before ico-prefix-primary"
          >
            <CommonRollingCounter
              :value="Math.floor(bettingStats.upWin / 1000)"
              suffix="K"
              typography="p1"
            />
          </span>
        </div>
        <!-- UP 참여자 목록 (모바일) -->
        <div class="betting-status__users">
          <AnimatePresence>
            <motion.div
              v-for="participant in bettingStats.upParticipants"
              :key="participant.id"
              class="betting-status__item"
              :class="{ 'betting-status__item--my': participant.isMy }"
              :initial="{ opacity: 0, scale: 0, x: 0, y: 0 }"
              :animate="{ opacity: 1, scale: 1, x: 0, y: 0 }"
              :exit="{ opacity: 0, scale: 0 }"
              :transition="{
                type: 'spring',
                stiffness: 300,
                damping: 15,
                mass: 0.8,
                bounce: 0.6,
              }"
            >
              <img
                src="@/assets/images/user.png"
                :alt="participant.name"
                class="betting-status__item-thumb"
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>

      <!-- UP 결과 영역 (게임 종료 시 표시) -->
      <motion.div
        v-if="bettingStats.upShowResult"
        class="betting-status__result"
        :class="{ 'betting-status__result--complete': upAnimationComplete }"
        :initial="{ opacity: 0, width: '50%' }"
        :animate="
          upAnimationFullyComplete
            ? isUpWinner
              ? { opacity: 1, width: '100%' }
              : { opacity: 1, width: '0' }
            : bothCountAnimationsComplete
            ? isUpWinner
              ? { opacity: 1, width: upResultExpanded ? '100%' : '50%' }
              : { opacity: 1, width: upResultExpanded ? '0' : '50%' }
            : { opacity: 1, width: '50%' }
        "
        :transition="
          upAnimationFullyComplete
            ? { duration: 0 }
            : bothCountAnimationsComplete && upResultExpanded
            ? { type: 'spring', stiffness: 250, damping: 30, mass: 0.8 }
            : resultEnterTransition
        "
        style="overflow: hidden"
      >
        <!-- 결과 배경 blob -->
        <AnimatePresence>
          <motion.i
            v-if="upAnimationComplete"
            key="blob"
            class="betting-status__result-blob"
            :initial="{ scale: 0, opacity: 0 }"
            :animate="{ scale: 1, opacity: 0.6 }"
            :transition="{
              type: 'spring',
              stiffness: 300,
              damping: 20,
              mass: 0.5,
              bounce: 0.2,
              delay: 0.55,
            }"
          >
          </motion.i>
        </AnimatePresence>
        <div class="betting-status__result-group">
          <AnimatePresence>
            <motion.div
              v-if="upAnimationComplete"
              key="value"
              class="betting-status__result-value"
              :initial="
                upAnimationFullyComplete
                  ? bothCountAnimationsComplete &&
                    !isUpWinner &&
                    upResultExpanded
                    ? { scale: 1, opacity: 0 }
                    : { scale: 1, opacity: 1 }
                  : { scale: 0, opacity: 0 }
              "
              :animate="
                bothCountAnimationsComplete && !isUpWinner && upResultExpanded
                  ? { scale: 1, opacity: 0 }
                  : { scale: 1, opacity: 1 }
              "
              :transition="
                upAnimationFullyComplete
                  ? { duration: 0 }
                  : bothCountAnimationsComplete &&
                    !isUpWinner &&
                    upResultExpanded
                  ? {
                      scale: {
                        type: 'spring',
                        stiffness: 300,
                        damping: 20,
                        mass: 0.5,
                        bounce: 0.2,
                        delay: 0.4,
                      },
                      opacity: { duration: 0 },
                    }
                  : {
                      type: 'spring',
                      stiffness: 300,
                      damping: 20,
                      mass: 0.5,
                      bounce: 0.2,
                      delay: 0.4,
                    }
              "
            >
              <strong class="betting-status__result-value-title">{{
                isUpWinner ? "승자" : "패자"
              }}</strong>
              <div class="betting-status__result-desc">
                <span class="ico-before ico-user-white">{{
                  upTotalPeopleCount
                }}</span>
                <span class="ico-before ico-prefix-white"
                  >{{ upDisplayAmountK }}K</span
                >
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
    <!-- DOWN 배팅 상태 영역 (모바일) -->
    <div class="betting-status__list betting-status__list--down">
      <!-- DOWN Content 영역 (참여자 목록) -->
      <motion.div
        :key="`down-content-mobile-${gameResetCounter}`"
        class="betting-status__content"
        :class="{
          'betting-status__content--animating': downContentAnimating,
        }"
        :initial="{ scale: 1, opacity: 1 }"
        :animate="
          bettingStats.downShowResult
            ? { scale: 0.8, opacity: 0 }
            : { scale: 1, opacity: 1 }
        "
        :transition="
          bettingStats.downShowResult
            ? contentExitTransition
            : instantTransition
        "
        style="transform-origin: center"
      >
        <!-- DOWN 정보 (참여자 수, 총 배팅금) -->
        <div
          class="betting-status__info"
          :class="{ 'betting-status__info--rendered': downInfoGroupRendered }"
        >
          <span
            class="betting-status__info-text betting-status__info-text--person ico-before ico-user-secondary"
          >
            <CommonRollingCounter
              :value="downTotalPeopleCount"
              typography="p1"
            />
          </span>
          <span
            class="betting-status__info-text betting-status__info-text--price ico-before ico-prefix-secondary"
          >
            <CommonRollingCounter
              :value="Math.floor(bettingStats.downLose / 1000)"
              suffix="K"
              typography="p1"
            />
          </span>
        </div>
        <!-- DOWN 참여자 목록 (모바일) -->
        <div class="betting-status__users">
          <AnimatePresence>
            <motion.div
              v-for="participant in bettingStats.downParticipants"
              :key="participant.id"
              class="betting-status__item"
              :class="{ 'betting-status__item--my': participant.isMy }"
              :initial="{ opacity: 0, scale: 0, x: 0, y: 0 }"
              :animate="{ opacity: 1, scale: 1, x: 0, y: 0 }"
              :exit="{ opacity: 0, scale: 0 }"
              :transition="{
                type: 'spring',
                stiffness: 300,
                damping: 15,
                mass: 0.8,
                bounce: 0.6,
              }"
            >
              <img
                src="@/assets/images/user.png"
                :alt="participant.name"
                class="betting-status__item-thumb"
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>

      <!-- DOWN 결과 영역 (게임 종료 시 표시) -->
      <motion.div
        v-if="bettingStats.downShowResult"
        class="betting-status__result"
        :class="{ 'betting-status__result--complete': downAnimationComplete }"
        :initial="{ opacity: 0, width: '50%' }"
        :animate="
          downAnimationFullyComplete
            ? isDownWinner
              ? { opacity: 1, width: '100%' }
              : { opacity: 1, width: '0' }
            : bothCountAnimationsComplete
            ? isDownWinner
              ? { opacity: 1, width: downResultExpanded ? '100%' : '50%' }
              : { opacity: 1, width: downResultExpanded ? '0' : '50%' }
            : { opacity: 1, width: '50%' }
        "
        :transition="
          downAnimationFullyComplete
            ? { duration: 0 }
            : bothCountAnimationsComplete && downResultExpanded
            ? { type: 'spring', stiffness: 250, damping: 30, mass: 0.8 }
            : resultEnterTransition
        "
        style="overflow: hidden"
      >
        <!-- 결과 배경 blob -->
        <AnimatePresence>
          <motion.i
            v-if="downAnimationComplete"
            key="blob"
            class="betting-status__result-blob"
            :initial="{ scale: 0, opacity: 0 }"
            :animate="{ scale: 1, opacity: 0.6 }"
            :transition="{
              type: 'spring',
              stiffness: 300,
              damping: 20,
              mass: 0.5,
              bounce: 0.2,
              delay: 0.55,
            }"
          >
          </motion.i>
        </AnimatePresence>
        <div class="betting-status__result-group">
          <AnimatePresence>
            <motion.div
              v-if="downAnimationComplete"
              key="value"
              class="betting-status__result-value"
              :initial="
                downAnimationFullyComplete
                  ? bothCountAnimationsComplete &&
                    !isDownWinner &&
                    downResultExpanded
                    ? { scale: 1, opacity: 0 }
                    : { scale: 1, opacity: 1 }
                  : { scale: 0, opacity: 0 }
              "
              :animate="
                bothCountAnimationsComplete &&
                !isDownWinner &&
                downResultExpanded
                  ? { scale: 1, opacity: 0 }
                  : { scale: 1, opacity: 1 }
              "
              :transition="
                downAnimationFullyComplete
                  ? { duration: 0 }
                  : bothCountAnimationsComplete &&
                    !isDownWinner &&
                    downResultExpanded
                  ? {
                      scale: {
                        type: 'spring',
                        stiffness: 300,
                        damping: 20,
                        mass: 0.5,
                        bounce: 0.2,
                        delay: 0.4,
                      },
                      opacity: { duration: 0 },
                    }
                  : {
                      type: 'spring',
                      stiffness: 300,
                      damping: 20,
                      mass: 0.5,
                      bounce: 0.2,
                      delay: 0.4,
                    }
              "
            >
              <strong class="betting-status__result-value-title">{{
                isDownWinner ? "승자" : "패자"
              }}</strong>
              <div class="betting-status__result-desc">
                <span class="ico-before ico-user-white">{{
                  downTotalPeopleCount
                }}</span>
                <span class="ico-before ico-prefix-white"
                  >{{ downDisplayAmountK }}K</span
                >
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  </template>

  <!-- 승리 시 UI -->
  <AnimatePresence>
    <motion.div
      v-show="showWinUI"
      key="win-ui"
      class="betting-status__win"
      :initial="{ opacity: 0 }"
      :animate="{ opacity: 1 }"
      :exit="{ opacity: 0 }"
      :transition="{ duration: 0.3 }"
    >
      <div class="betting-status__win-content">
        <!-- BG Image -->
        <motion.div
          class="betting-status__win-bg"
          :initial="{ scale: 0, opacity: 0 }"
          :animate="{ scale: 1, opacity: 1 }"
          :transition="{
            type: 'spring',
            stiffness: 400,
            damping: 20,
            mass: 0.5,
            bounce: 0.5,
            delay: 0,
          }"
        >
          <motion.div
            class="betting-status__win-bg"
            :animate="{
              scale: isMobile
                ? [1, 1.008, 1, 1.005, 1]
                : [1, 1.02, 1, 1.015, 1],
            }"
            :transition="{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.8,
            }"
          >
            <img src="@/assets/images/winner_bg.png" alt="WINNER" />
          </motion.div>
        </motion.div>

        <!-- 코인 이미지 -->
        <motion.div
          class="betting-status__win-coin betting-status__win-coin--01"
          :initial="{ scale: 0, opacity: 0, x: -30, y: -40, rotate: -45 }"
          :animate="{
            scale: 1,
            opacity: 1,
            x: 0,
            y: isMobile ? [0, -8, 0, -5, 0] : [0, -15, 0, -10, 0],
            rotate: 0,
          }"
          :transition="{
            scale: {
              type: 'spring',
              stiffness: 500,
              damping: 12,
              mass: 0.4,
              bounce: 0.65,
              delay: 0.4,
            },
            opacity: {
              duration: 0.2,
              delay: 0.4,
            },
            x: {
              type: 'spring',
              stiffness: 500,
              damping: 12,
              mass: 0.4,
              bounce: 0.65,
              delay: 0.4,
            },
            rotate: {
              type: 'spring',
              stiffness: 450,
              damping: 15,
              mass: 0.5,
              bounce: 0.6,
              delay: 0.4,
            },
            y: {
              duration: 2.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.9,
            },
          }"
        >
          <img src="@/assets/images/coin_01.svg" alt="" />
        </motion.div>
        <motion.div
          class="betting-status__win-coin betting-status__win-coin--02"
          :initial="{ scale: 0, opacity: 0, x: 40, y: -30, rotate: 50 }"
          :animate="{
            scale: 1,
            opacity: 1,
            x: isMobile ? [0, 6, 0, 4, 0] : [0, 12, 0, 8, 0],
            y: 0,
            rotate: 0,
          }"
          :transition="{
            scale: {
              type: 'spring',
              stiffness: 480,
              damping: 13,
              mass: 0.45,
              bounce: 0.62,
              delay: 0.48,
            },
            opacity: {
              duration: 0.2,
              delay: 0.48,
            },
            x: {
              duration: 2.8,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1.0,
            },
            y: {
              type: 'spring',
              stiffness: 480,
              damping: 13,
              mass: 0.45,
              bounce: 0.62,
              delay: 0.48,
            },
            rotate: {
              type: 'spring',
              stiffness: 430,
              damping: 16,
              mass: 0.55,
              bounce: 0.58,
              delay: 0.48,
            },
          }"
        >
          <img src="@/assets/images/coin_02.svg" alt="" />
        </motion.div>
        <motion.div
          class="betting-status__win-coin betting-status__win-coin--03"
          :initial="{ scale: 0, opacity: 0, x: -40, y: 50, rotate: -40 }"
          :animate="{
            scale: 1,
            opacity: 1,
            x: 0,
            y: isMobile ? [0, 9, 0, 6, 0] : [0, 18, 0, 12, 0],
            rotate: 0,
          }"
          :transition="{
            scale: {
              type: 'spring',
              stiffness: 520,
              damping: 11,
              mass: 0.35,
              bounce: 0.68,
              delay: 0.56,
            },
            opacity: {
              duration: 0.2,
              delay: 0.56,
            },
            x: {
              type: 'spring',
              stiffness: 520,
              damping: 11,
              mass: 0.35,
              bounce: 0.68,
              delay: 0.56,
            },
            y: {
              duration: 3.2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1.1,
            },
            rotate: {
              type: 'spring',
              stiffness: 460,
              damping: 14,
              mass: 0.48,
              bounce: 0.63,
              delay: 0.56,
            },
          }"
        >
          <img src="@/assets/images/coin_03.svg" alt="" />
        </motion.div>
        <motion.div
          class="betting-status__win-coin betting-status__win-coin--04"
          :initial="{ scale: 0, opacity: 0, x: 35, y: 45, rotate: 48 }"
          :animate="{
            scale: 1,
            opacity: 1,
            x: isMobile ? [0, -5, 0, -8, 0] : [0, -10, 0, -15, 0],
            y: 0,
            rotate: 0,
          }"
          :transition="{
            scale: {
              type: 'spring',
              stiffness: 490,
              damping: 12,
              mass: 0.42,
              bounce: 0.64,
              delay: 0.64,
            },
            opacity: {
              duration: 0.2,
              delay: 0.64,
            },
            x: {
              duration: 2.6,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1.2,
            },
            y: {
              type: 'spring',
              stiffness: 490,
              damping: 12,
              mass: 0.42,
              bounce: 0.64,
              delay: 0.64,
            },
            rotate: {
              type: 'spring',
              stiffness: 440,
              damping: 15,
              mass: 0.52,
              bounce: 0.6,
              delay: 0.64,
            },
          }"
        >
          <img src="@/assets/images/coin_04.svg" alt="" />
        </motion.div>

        <!-- 금액 -->
        <motion.div
          class="betting-status__win-value"
          :initial="{ scale: 0, opacity: 0, y: 50 }"
          :animate="{ scale: 1, opacity: 1, y: 0 }"
          :transition="{
            type: 'spring',
            stiffness: 300,
            damping: 20,
            mass: 0.6,
            bounce: 0.6,
            delay: 0.5,
          }"
        >
          <span>₩</span>
          <strong>{{ displayWinAmount }}</strong>
        </motion.div>
      </div>
    </motion.div>
  </AnimatePresence>
</template>

<style scoped></style>
